import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, euro, fmtDate, API } from "@/lib/api";
import { StatusBadge } from "@/pages/ClientDashboard";
import ServicesManager from "@/pages/ServicesManager";
import FinanceDashboard from "@/pages/FinanceDashboard";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["confirmed", "checked_in", "in_progress", "completed", "no_show", "cancelled_by_salon"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "receptionist";
  const [overview, setOverview] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState(isManager ? "overview" : "calendar");
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [uploading, setUploading] = useState("");
  const [newLook, setNewLook] = useState({ title: "", image: "", price: 45, duration_min: 60, art_level: "Media", category_id: "design" });
  const [clientSearch, setClientSearch] = useState("");
  const [openClient, setOpenClient] = useState(null);
  const [pendingLooks, setPendingLooks] = useState([]);
  const [clientPhotos, setClientPhotos] = useState([]);
  const [uploadingCW, setUploadingCW] = useState(false);

  const loadLooks = () => api.get("/admin/looks?status=pending").then((r) => setPendingLooks(r.data)).catch(() => {});

  const loadBookings = (d) => api.get(`/admin/bookings?date=${d}`).then((r) => setBookings(r.data)).catch(() => {});
  const loadMedia = () => {
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (isManager) {
      api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
      api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    }
    loadBookings(date);
    loadMedia();
    if (isManager) loadLooks();
  }, [user]);

  const changeStatus = async (id, status) => {
    try { await api.post(`/admin/bookings/${id}/status`, { status }); toast.success("Stato aggiornato"); loadBookings(date); if (isManager) api.get("/admin/overview").then((r) => setOverview(r.data)); }
    catch { toast.error("Errore"); }
  };

  const uploadFile = async (fileList, ctx) => {
    const file = fileList?.[0];
    if (!file) return null;
    setUploading(ctx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return `${API}/files/${data.path}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
      return null;
    } finally { setUploading(""); }
  };

  const uploadPortrait = async (sid, files) => {
    const url = await uploadFile(files, "portrait");
    if (!url) return;
    try { await api.patch(`/admin/staff/${sid}`, { portrait: url }); toast.success("Ritratto aggiornato"); loadMedia(); }
    catch { toast.error("Errore nell'aggiornamento"); }
  };
  const uploadLookImage = async (files) => {
    const url = await uploadFile(files, "look");
    if (url) setNewLook((p) => ({ ...p, image: url }));
  };
  const addLook = async () => {
    if (!newLook.title || !newLook.image) { toast.error("Inserisci titolo e immagine"); return; }
    try { await api.post("/admin/gallery", { ...newLook, price: Number(newLook.price), duration_min: Number(newLook.duration_min), tags: [] }); toast.success("Look aggiunto"); setNewLook({ title: "", image: "", price: 45, duration_min: 60, art_level: "Media", category_id: "design" }); loadMedia(); }
    catch { toast.error("Errore"); }
  };
  const removeLook = async (id) => {
    try { await api.delete(`/admin/gallery/${id}`); toast.success("Look rimosso"); loadMedia(); }
    catch { toast.error("Errore"); }
  };

  const [showAdd, setShowAdd] = useState(false);
  const [manual, setManual] = useState({ start_time: "11:30", duration_min: 60, kind: "appointment", client_name: "", client_phone: "", client_email: "", service_name: "", notes: "", price: "", mark_paid: false, allow_overbooking: false });
  const openAdd = () => { setTab("calendar"); setShowAdd(true); };
  const addManual = async () => {
    if (manual.kind === "appointment" && !manual.client_name) { toast.error("Inserisci il nome del cliente"); return; }
    try {
      await api.post("/admin/manual-booking", { ...manual, date, duration_min: Number(manual.duration_min), price: Number(manual.price) || 0 });
      toast.success(manual.kind === "block" ? "Orario bloccato" : "Appuntamento aggiunto · cliente salvato in anagrafica");
      setManual({ start_time: "11:30", duration_min: 60, kind: "appointment", client_name: "", client_phone: "", client_email: "", service_name: "", notes: "", price: "", mark_paid: false, allow_overbooking: false });
      setShowAdd(false); loadBookings(date);
      if (isManager) { api.get("/admin/overview").then((r) => setOverview(r.data)); api.get("/admin/clients").then((r) => setClients(r.data)); }
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const deleteBooking = async (id) => {
    if (!window.confirm("Eliminare questo impegno dall'agenda?")) return;
    try { await api.delete(`/admin/bookings/${id}`); toast.success("Eliminato"); loadBookings(date); if (isManager) api.get("/admin/overview").then((r) => setOverview(r.data)); }
    catch { toast.error("Errore"); }
  };

  const [editClient, setEditClient] = useState(null);
  const saveClient = async () => {
    try {
      await api.patch(`/admin/clients/${editClient.id}`, {
        name: editClient.name, surname: editClient.surname, phone: editClient.phone,
        email: editClient.email, birthday: editClient.birthday || null,
        deposit_policy: editClient.deposit_policy || "inherit", deposit_note: editClient.deposit_note || null,
      });
      toast.success("Cliente aggiornato");
      setEditClient(null);
      api.get("/admin/clients").then((r) => setClients(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const deleteClient = async (c) => {
    if (!window.confirm(`Eliminare definitivamente ${c.name} ${c.surname} dall'anagrafica?`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Cliente eliminato");
      api.get("/admin/clients").then((r) => setClients(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const moderateLook = async (id, action) => {
    try { await api.post(`/admin/looks/${id}/moderate`, { action }); toast.success(action === "approve" ? "Look pubblicato" : "Look rifiutato"); loadLooks(); }
    catch { toast.error("Errore"); }
  };

  const openEditClient = (c) => {
    setEditClient({ ...c, birthday: c.birthday || "" });
    api.get(`/admin/clients/${c.id}/wardrobe`).then((r) => setClientPhotos(r.data)).catch(() => setClientPhotos([]));
  };
  const uploadClientWardrobe = async (files) => {
    const file = files?.[0];
    if (!file || !editClient) return;
    setUploadingCW(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/clients/${editClient.id}/wardrobe`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto aggiunta al Wardrobe del cliente");
      api.get(`/admin/clients/${editClient.id}/wardrobe`).then((r) => setClientPhotos(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setUploadingCW(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <span className="kima-overline text-burgundy">Dashboard</span>
      <h1 className="font-serif text-4xl md:text-5xl text-espresso mt-2 mb-8">Gestione KIMA</h1>
      <div className="flex gap-2 mb-10 border-b border-espresso/10">
        {[["overview", "Panoramica"], ["calendar", "Calendario"], ["finance", "Incassi"], ["clients", "Clienti"], ["services", "Servizi"], ["media", "Media & Foto"], ["looks", "KIMA Looks"]].filter(([k]) => isManager || k === "calendar" || k === "media" || k === "looks").map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-burgundy text-espresso" : "border-transparent text-taupe hover:text-espresso"}`}>{l}{k === "looks" && pendingLooks.length > 0 && <span className="ml-1.5 text-xs bg-burgundy text-ivory rounded-full px-1.5 py-0.5" data-testid="looks-pending-badge">{pendingLooks.length}</span>}</button>
        ))}
      </div>

      {tab === "overview" && overview && (
        <>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-2xl text-espresso">Oggi in salone</h3>
            <button onClick={openAdd} data-testid="overview-add-appointment" className="bg-espresso text-ivory px-5 py-2.5 rounded-sm hover:bg-burgundy transition-colors text-sm">+ Nuovo appuntamento</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[["Appuntamenti oggi", overview.today_count], ["Ricavo oggi", euro(overview.today_revenue)], ["Acconti raccolti", euro(overview.deposits_collected)], ["Clienti totali", overview.total_clients], ["Prenotazioni totali", overview.total_bookings], ["Cancellazioni", overview.cancellations], ["No-show", overview.no_shows]].map(([l, v]) => (
              <div key={l} className="border border-espresso/10 bg-offwhite rounded-sm p-5"><div className="kima-overline text-taupe">{l}</div><div className="font-serif text-3xl text-espresso mt-2">{v}</div></div>
            ))}
          </div>
          <h3 className="font-serif text-2xl text-espresso mb-4">Appuntamenti di oggi</h3>
          <div className="space-y-2">
            {overview.today_bookings.length === 0 && <p className="text-espresso/50">Nessun appuntamento oggi.</p>}
            {overview.today_bookings.map((b) => (<div key={b.id} className="border border-espresso/10 bg-offwhite rounded-sm p-4 flex justify-between items-center"><div><span className="font-medium text-espresso">{b.start_time}</span> · {b.service_name} · <span className="text-espresso/60">{b.client_name}</span></div><StatusBadge s={b.status} /></div>))}
          </div>
        </>
      )}

      {tab === "finance" && isManager && <FinanceDashboard />}

      {tab === "calendar" && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <input type="date" value={date} data-testid="admin-date" onChange={(e) => { setDate(e.target.value); loadBookings(e.target.value); }} className="border border-espresso/20 rounded-sm px-4 py-2 bg-offwhite" />
            <button onClick={() => setShowAdd(!showAdd)} data-testid="toggle-add-appointment" className="bg-espresso text-ivory px-5 py-2 rounded-sm hover:bg-burgundy transition-colors text-sm">{showAdd ? "Chiudi" : "+ Aggiungi appuntamento / blocco"}</button>
          </div>

          {showAdd && (
            <div className="border border-espresso/10 bg-offwhite rounded-sm p-6 mb-6 animate-fade-up">
              <div className="flex gap-2 mb-4">
                {[["appointment", "Appuntamento"], ["block", "Blocca orario"]].map(([k, l]) => (
                  <button key={k} onClick={() => setManual({ ...manual, kind: k })} data-testid={`manual-kind-${k}`} className={`px-4 py-2 rounded-sm text-sm border transition-colors ${manual.kind === k ? "bg-espresso text-ivory border-espresso" : "border-espresso/20 hover:border-espresso"}`}>{l}</button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div><label className="kima-overline text-taupe">Orario inizio</label><input type="time" value={manual.start_time} data-testid="manual-start" onChange={(e) => setManual({ ...manual, start_time: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" /></div>
                <div><label className="kima-overline text-taupe">Durata (min)</label><input type="number" value={manual.duration_min} data-testid="manual-duration" onChange={(e) => setManual({ ...manual, duration_min: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" /></div>
                {manual.kind === "appointment" && (<>
                  <div><label className="kima-overline text-taupe">Cliente</label><input value={manual.client_name} data-testid="manual-client" onChange={(e) => setManual({ ...manual, client_name: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" /></div>
                  <div><label className="kima-overline text-taupe">Servizio</label><input value={manual.service_name} data-testid="manual-service" onChange={(e) => setManual({ ...manual, service_name: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" /></div>
                  <div><label className="kima-overline text-taupe">Prezzo / incasso €</label><input type="number" value={manual.price} data-testid="manual-price" onChange={(e) => setManual({ ...manual, price: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" /></div>
                  <div className="lg:col-span-2"><label className="kima-overline text-taupe">Email (facoltativa)</label><input type="email" value={manual.client_email} data-testid="manual-email" onChange={(e) => setManual({ ...manual, client_email: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" /></div>
                  <div className="lg:col-span-2"><label className="kima-overline text-taupe">Telefono</label><input value={manual.client_phone} data-testid="manual-phone" onChange={(e) => setManual({ ...manual, client_phone: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" /></div>
                </>)}
                <div className="lg:col-span-2"><label className="kima-overline text-taupe">Note</label><input value={manual.notes} data-testid="manual-notes" onChange={(e) => setManual({ ...manual, notes: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" /></div>
              </div>
              {manual.kind === "appointment" && (
                <div className="mt-4 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-espresso/70"><input type="checkbox" data-testid="manual-mark-paid" checked={manual.mark_paid} onChange={(e) => setManual({ ...manual, mark_paid: e.target.checked })} /> Già pagato in salone (segna come completato e conta subito negli Incassi)</label>
                  <label className="flex items-center gap-2 text-sm text-espresso/70"><input type="checkbox" data-testid="manual-overbooking" checked={manual.allow_overbooking} onChange={(e) => setManual({ ...manual, allow_overbooking: e.target.checked })} /> Consenti sovrapposizione (overbooking) — inserisci anche se l'orario è già occupato</label>
                </div>
              )}
              <button onClick={addManual} data-testid="manual-submit" className="mt-5 bg-espresso text-ivory px-6 py-3 rounded-sm hover:bg-burgundy transition-colors text-sm">{manual.kind === "block" ? "Blocca orario" : "Aggiungi appuntamento"}</button>
              <p className="mt-3 text-xs text-espresso/50">Come admin puoi inserire appuntamenti a qualsiasi ora, anche fuori dall'orario di apertura o nei giorni di chiusura, e sovrapporli ad altri (overbooking). Il cliente inserito viene salvato automaticamente nell'anagrafica (tab Clienti). Ai clienti online restano visibili solo gli slot liberi dentro l'orario.</p>
            </div>
          )}

          <div className="space-y-3">
            {bookings.length === 0 && <p className="text-espresso/50">Nessun appuntamento per {fmtDate(date)}.</p>}
            {bookings.map((b) => (
              <div key={b.id} className={`border rounded-sm p-5 flex flex-wrap justify-between items-center gap-4 ${b.is_block ? "border-espresso bg-espresso/5" : "border-espresso/10 bg-offwhite"}`}>
                <div>
                  <div className="font-serif text-xl text-espresso">{b.start_time}–{b.end_time} · {b.service_name} {b.overbooked && <span className="text-xs text-burgundy align-middle border border-burgundy/40 rounded-sm px-1.5 py-0.5 ml-1">overbooking</span>}</div>
                  <div className="text-sm text-espresso/60">{b.is_block ? (b.notes || "Orario non prenotabile") : `${b.client_name}${b.client_phone ? " · " + b.client_phone : ""} · ${euro(b.price)}${b.amount_paid ? " (acconto " + euro(b.amount_paid) + ")" : ""}`}</div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge s={b.status} />
                  {!b.is_block && (
                    <select value={b.status} onChange={(e) => changeStatus(b.id, e.target.value)} data-testid={`admin-status-${b.id}`} className="border border-espresso/20 rounded-sm px-3 py-2 text-sm bg-offwhite">
                      {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  )}
                  <button onClick={() => deleteBooking(b.id)} data-testid={`delete-booking-${b.id}`} className="text-kimaerror text-sm hover:underline">Elimina</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "clients" && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p className="text-sm text-espresso/60">{clients.length} clienti registrati · i dati restano salvati nel CRM.</p>
            <input value={clientSearch} data-testid="client-search" onChange={(e) => setClientSearch(e.target.value)} placeholder="Cerca per nome, email o telefono" className="border border-espresso/20 rounded-sm px-4 py-2 bg-offwhite text-sm w-full sm:w-80" />
          </div>
          <div className="overflow-x-auto border border-espresso/10 rounded-sm">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-taupe border-b border-espresso/10 bg-offwhite">
                <th className="py-3 px-4 kima-overline font-medium">Cliente</th>
                <th className="py-3 px-4 kima-overline font-medium">Contatti</th>
                <th className="py-3 px-4 kima-overline font-medium">Compleanno</th>
                <th className="py-3 px-4 kima-overline font-medium text-right">Visite</th>
                <th className="py-3 px-4 kima-overline font-medium text-right">Spesa</th>
                <th className="py-3 px-4 kima-overline font-medium text-right">Punti</th>
                <th className="py-3 px-4 kima-overline font-medium text-right">Azioni</th>
              </tr></thead>
              <tbody>
                {clients.filter((c) => {
                  const q = clientSearch.toLowerCase();
                  return !q || `${c.name} ${c.surname} ${c.email} ${c.phone}`.toLowerCase().includes(q);
                }).map((c) => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => setOpenClient(openClient === c.id ? null : c.id)} className="border-b border-espresso/5 hover:bg-offwhite cursor-pointer align-top">
                    <td className="py-3 px-4"><div className="text-espresso font-medium">{c.name} {c.surname}</div><div className="text-taupe text-xs">{c.kima_id} · {c.level}{c.deposit_policy && c.deposit_policy !== "inherit" && <span className={`ml-2 px-1.5 py-0.5 rounded-sm ${c.deposit_policy === "waived" ? "bg-kimasuccess/15 text-kimasuccess" : "bg-burgundy/15 text-burgundy"}`}>{c.deposit_policy === "waived" ? "esente acconto" : "acconto obbligatorio"}</span>}</div>
                      {openClient === c.id && c.next_appointment && <div className="text-xs text-burgundy mt-1">Prossimo: {fmtDate(c.next_appointment.date)} {c.next_appointment.start_time} · {c.next_appointment.service_name}</div>}
                    </td>
                    <td className="py-3 px-4 text-espresso/70"><div>{c.email}</div><div>{c.phone || "—"}</div></td>
                    <td className="py-3 px-4 text-espresso/70">{c.birthday ? fmtDate(c.birthday) : "—"}</td>
                    <td className="py-3 px-4 text-right text-espresso/70">{c.completed_count}/{c.bookings_count}</td>
                    <td className="py-3 px-4 text-right text-espresso font-medium">{euro(c.total_spend)}</td>
                    <td className="py-3 px-4 text-right text-espresso/70">{c.points}</td>
                    <td className="py-3 px-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => openEditClient(c)} data-testid={`client-edit-${c.id}`} className="text-espresso hover:text-burgundy text-xs mr-3 underline">Modifica</button>
                      <button onClick={() => deleteClient(c)} data-testid={`client-delete-${c.id}`} className="text-kimaerror text-xs underline">Elimina</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {editClient && (
            <div className="fixed inset-0 bg-espresso/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEditClient(null)}>
              <div className="bg-offwhite rounded-sm p-8 w-full max-w-md animate-fade-up" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-serif text-2xl text-espresso mb-6">Modifica cliente</h3>
                <div className="space-y-4">
                  {[["name", "Nome"], ["surname", "Cognome"], ["phone", "Telefono"], ["email", "Email"]].map(([k, l]) => (
                    <div key={k}><label className="kima-overline text-taupe">{l}</label><input value={editClient[k] || ""} data-testid={`edit-client-${k}`} onChange={(e) => setEditClient({ ...editClient, [k]: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso" /></div>
                  ))}
                  <div><label className="kima-overline text-taupe">Compleanno</label><input type="date" value={editClient.birthday || ""} data-testid="edit-client-birthday" onChange={(e) => setEditClient({ ...editClient, birthday: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso" /></div>
                  <div>
                    <label className="kima-overline text-taupe">Politica acconto</label>
                    <select value={editClient.deposit_policy || "inherit"} data-testid="edit-client-policy" onChange={(e) => setEditClient({ ...editClient, deposit_policy: e.target.value })} className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso">
                      <option value="inherit">Regola standard</option>
                      <option value="required">Acconto obbligatorio</option>
                      <option value="waived">Esente da acconto</option>
                    </select>
                    <p className="text-xs text-espresso/50 mt-1">{editClient.deposit_policy === "waived" ? "Prenotazione immediata, salvo servizi non derogabili." : editClient.deposit_policy === "required" ? "Acconto sempre richiesto per confermare." : "Segue le regole del servizio e del salone."}</p>
                  </div>
                  <div><label className="kima-overline text-taupe">Nota interna (facoltativa)</label><input value={editClient.deposit_note || ""} data-testid="edit-client-note" onChange={(e) => setEditClient({ ...editClient, deposit_note: e.target.value })} placeholder="es. cliente abituale, credito aziendale…" className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso text-espresso" /></div>
                  <div className="border-t border-espresso/10 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="kima-overline text-taupe">KIMA Wardrobe del cliente</label>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-espresso underline hover:text-burgundy">
                        {uploadingCW ? "Caricamento…" : "+ Carica foto"}
                        <input type="file" accept="image/*" data-testid="client-wardrobe-upload" className="hidden" onChange={(e) => uploadClientWardrobe(e.target.files)} />
                      </label>
                    </div>
                    {clientPhotos.length === 0 ? <p className="text-xs text-espresso/40">Nessuna foto nell'archivio del cliente.</p> : (
                      <div className="grid grid-cols-4 gap-2">
                        {clientPhotos.slice(0, 8).map((p) => (<img key={p.id} src={p.image_url} alt="look" data-testid={`client-wardrobe-photo-${p.id}`} className="w-full aspect-square object-cover rounded-sm" />))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 mt-8">
                  <button onClick={saveClient} data-testid="edit-client-save" className="flex-1 bg-espresso text-ivory py-3 rounded-sm hover:bg-burgundy transition-colors text-sm">Salva</button>
                  <button onClick={() => setEditClient(null)} className="px-6 py-3 border border-espresso/20 rounded-sm hover:border-espresso transition-colors text-sm">Annulla</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "services" && isManager && <ServicesManager />}

      {tab === "looks" && (
        <div data-testid="looks-moderation-panel">
          <h3 className="font-serif text-2xl text-espresso mb-1">Moderazione KIMA Looks</h3>
          <p className="text-sm text-espresso/60 mb-6">Proposte dei clienti in attesa di approvazione. Approva per pubblicare nella community, rifiuta per nascondere.</p>
          {pendingLooks.length === 0 && <p className="text-espresso/50">Nessuna proposta in attesa.</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pendingLooks.map((l) => (
              <div key={l.id} data-testid={`pending-look-${l.id}`} className="border border-espresso/10 bg-offwhite rounded-sm overflow-hidden">
                <img src={l.image} alt={l.title} className="w-full aspect-square object-cover" />
                <div className="p-4">
                  <div className="font-serif text-xl text-espresso">{l.title}</div>
                  <div className="text-xs text-taupe mt-1">Proposto da {l.submitter_name || "—"} · appare come {l.attribution === "name" && l.display_name ? l.display_name : "Anonimo"}</div>
                  {l.service_name && <div className="text-xs text-espresso/60 mt-1">Servizio: {l.service_name}</div>}
                  {l.caption && <p className="text-sm text-espresso/70 mt-2">{l.caption}</p>}
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => moderateLook(l.id, "approve")} data-testid={`look-approve-${l.id}`} className="flex-1 bg-espresso text-ivory py-2 rounded-sm text-sm hover:bg-burgundy transition-colors">Approva</button>
                    <button onClick={() => moderateLook(l.id, "reject")} data-testid={`look-reject-${l.id}`} className="px-4 py-2 border border-kimaerror/40 text-kimaerror rounded-sm text-sm hover:bg-kimaerror hover:text-ivory transition-colors">Rifiuta</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "media" && (
        <div className="space-y-12">
          <section>
            <h3 className="font-serif text-2xl text-espresso mb-1">Ritratto nail artist</h3>
            <p className="text-sm text-espresso/60 mb-5">Carica o aggiorna la foto profilo mostrata nel sito.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {staff.map((a) => (
                <div key={a.id} className="border border-espresso/10 bg-offwhite rounded-sm overflow-hidden">
                  <img src={a.portrait} alt={a.name} className="w-full aspect-[4/3] object-cover" />
                  <div className="p-4">
                    <div className="font-serif text-xl text-espresso">{a.name}</div>
                    <div className="text-sm text-espresso/60 mb-3">{a.role_title}</div>
                    <label className="inline-block cursor-pointer bg-espresso text-ivory text-sm px-4 py-2 rounded-sm hover:bg-burgundy transition-colors">
                      {uploading === "portrait" ? "Caricamento…" : "Carica nuova foto"}
                      <input type="file" accept="image/*" data-testid={`upload-portrait-${a.id}`} className="hidden" onChange={(e) => uploadPortrait(a.id, e.target.files)} />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h3 className="font-serif text-2xl text-espresso mb-1">Galleria — aggiungi un look</h3>
            <p className="text-sm text-espresso/60 mb-5">Carica le foto dei tuoi lavori. Appariranno nella galleria prenotabile.</p>
            <div className="border border-espresso/10 bg-offwhite rounded-sm p-6 grid md:grid-cols-2 gap-5 mb-8">
              <div className="space-y-4">
                <input className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" placeholder="Titolo del look" data-testid="look-title" value={newLook.title} onChange={(e) => setNewLook({ ...newLook, title: e.target.value })} />
                <div className="flex gap-3">
                  <input type="number" className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" placeholder="Prezzo €" data-testid="look-price" value={newLook.price} onChange={(e) => setNewLook({ ...newLook, price: e.target.value })} />
                  <input type="number" className="w-full border-b border-espresso/20 bg-transparent py-2 focus:outline-none focus:border-espresso" placeholder="Durata min" data-testid="look-duration" value={newLook.duration_min} onChange={(e) => setNewLook({ ...newLook, duration_min: e.target.value })} />
                </div>
                <label className="inline-block cursor-pointer border border-espresso text-espresso text-sm px-4 py-2 rounded-sm hover:bg-espresso hover:text-ivory transition-colors">
                  {uploading === "look" ? "Caricamento…" : "Carica immagine"}
                  <input type="file" accept="image/*" data-testid="upload-look" className="hidden" onChange={(e) => uploadLookImage(e.target.files)} />
                </label>
                <button onClick={addLook} data-testid="add-look" className="block w-full bg-espresso text-ivory py-3 rounded-sm hover:bg-burgundy transition-colors text-sm">Aggiungi alla galleria</button>
              </div>
              <div className="flex items-center justify-center border border-dashed border-espresso/20 rounded-sm min-h-[180px]">
                {newLook.image ? <img src={newLook.image} alt="anteprima" className="max-h-52 object-contain" /> : <span className="text-taupe text-sm">Anteprima immagine</span>}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((l) => (
                <div key={l.id} className="border border-espresso/10 rounded-sm overflow-hidden relative group">
                  <img src={l.image} alt={l.title} className="w-full aspect-square object-cover" />
                  <div className="p-3 flex justify-between items-center"><span className="font-serif text-espresso truncate">{l.title}</span>
                    <button onClick={() => removeLook(l.id)} data-testid={`delete-look-${l.id}`} className="text-kimaerror text-xs hover:underline">Rimuovi</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
